import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import logo from '../../assets/bmp-logo-dark.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.access_token) {
        // Store token and user data
        localStorage.setItem('admin_token', response.data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        
        toast({
          title: "Success",
          description: `Welcome back, ${response.data.user.full_name}!`,
        });
        
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid email or password",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email Required",
        description: "Please enter your email address",
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password/request`, {
        email
      });

      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for the reset code. (Check spam folder if not received)",
      });
      
      setForgotPasswordMode(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to send reset email",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#023E8A] to-[#00B4D8] flex items-center justify-center px-4 relative">
      {/* Back to Homepage Button - Top Left */}
      <button
        onClick={() => window.location.href = '/'}
        className="absolute top-6 left-6 flex items-center gap-2 text-white hover:text-gray-200 transition-colors group"
      >
        <div className="p-2 rounded-lg bg-white/10 group-hover:bg-white/20 transition-colors">
          <Home className="w-5 h-5" />
        </div>
        <span className="font-medium hidden sm:inline">Back to Homepage</span>
      </button>

      {/* Language Switcher - Top Right */}
      <div className="absolute top-6 right-6">
        <div className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/20">
          EN
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={logo} alt="Grupo BMP" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">Admin Panel</h1>
            <p className="text-[#6C757D] mt-2">
              {forgotPasswordMode ? 'Reset your password' : 'Sign in to manage your platform'}
            </p>
          </div>

          {/* Login / Reset Form */}
          <form onSubmit={forgotPasswordMode ? handleForgotPassword : handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                className="w-full"
              />
            </div>

            {!forgotPasswordMode && (
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot Password Link */}
            {!forgotPasswordMode && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setForgotPasswordMode(true)}
                  className="text-sm text-[#00B4D8] hover:text-[#0096c7] font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white py-6 text-base font-semibold transition-colors"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : forgotPasswordMode ? (
                'Send Reset Email'
              ) : (
                'Login'
              )}
            </Button>

            {/* Back to Login Link */}
            {forgotPasswordMode && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setForgotPasswordMode(false)}
                  className="text-sm text-[#6C757D] hover:text-[#2B2D42] font-medium transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </div>
            )}
          </form>

          {/* Security Notice - NO CREDENTIALS EVER DISPLAYED */}
          {!forgotPasswordMode && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-[#023E8A] font-medium text-center">
                🔐 Use your administrator credentials to sign in
              </p>
            </div>
          )}

          {/* P8 — Premium Sign-up block (Hybrid Elite black + gold) */}
          {!forgotPasswordMode && (
            <div className="mt-6" data-testid="login-signup-block">
              <div className="relative my-6 flex items-center">
                <span className="flex-grow h-px bg-slate-200" />
                <span className="px-3 text-[10px] tracking-[0.32em] uppercase text-slate-500">Or register</span>
                <span className="flex-grow h-px bg-slate-200" />
              </div>

              <div
                className="relative rounded-xl overflow-hidden border border-[#C9A86A]/40 shadow-lg"
                style={{ background: 'linear-gradient(135deg,#0A0A0A 0%,#1F1B12 100%)' }}
              >
                <div
                  className="absolute inset-0 opacity-15"
                  style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(201,168,106,0.5), transparent 55%)' }}
                />
                <div className="relative p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] tracking-[0.32em] uppercase text-[#C9A86A]">Grupo BMP · New</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Create your account
                  </h3>
                  <p className="text-white/60 text-xs mt-1 mb-4">
                    Register your company, partnership or interest and our team will reach out.
                  </p>

                  <button
                    type="button"
                    onClick={() => navigate('/register/entity')}
                    data-testid="login-signup-company-btn"
                    className="w-full bg-[#C9A86A] hover:bg-[#B7945A] text-black font-semibold py-3 rounded-lg transition-colors text-sm"
                  >
                    Register Company
                  </button>

                  <div className="grid grid-cols-3 gap-1.5 mt-2.5">
                    <button type="button" onClick={() => navigate('/register/entity?type=individual')}
                      data-testid="login-signup-individual-btn"
                      className="text-[11px] py-2 rounded-md border border-white/15 bg-black/40 text-white/80 hover:bg-black/60 hover:border-[#C9A86A]/50 transition-colors">
                      Individual
                    </button>
                    <button type="button" onClick={() => navigate('/register/entity?type=organization')}
                      data-testid="login-signup-organization-btn"
                      className="text-[11px] py-2 rounded-md border border-white/15 bg-black/40 text-white/80 hover:bg-black/60 hover:border-[#C9A86A]/50 transition-colors">
                      Organization
                    </button>
                    <button type="button" onClick={() => navigate('/register/entity?role=supplier')}
                      data-testid="login-signup-supplier-btn"
                      className="text-[11px] py-2 rounded-md border border-white/15 bg-black/40 text-white/80 hover:bg-black/60 hover:border-[#C9A86A]/50 transition-colors">
                      Supplier / Partner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-[#6C757D] mt-6">
            Grupo BMP Admin Panel © 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
